<template>
  <layout-header />
  <layout-sidebar />

  <div class="page-wrapper dq-loans">
    <div class="content">
      <!-- Header -->
      <div class="dq-topbar mb-3">
        <div
          class="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-2"
        >
          <breadcrumb
            :title="pageTitle"
            :text="pageSection"
            :text1="pageCrumb"
          />

          <div class="d-flex gap-2">
            <button
              v-if="canManageLoanTypes"
              class="btn dq-btn-ghost"
              @click="openTypes"
            >
              <i class="ti ti-list me-1"></i> Loan Types
            </button>
            <a v-if="canCreateLoan" class="btn btn-primary" @click="openCreate">
              <i class="ti ti-plus me-1"></i> New Loan
            </a>
          </div>
        </div>

        <div class="dq-subtext mt-2">
          <i class="ti ti-bolt me-1"></i>
          {{ pageSubtext }}
        </div>
      </div>

      <!-- Summary -->
      <div class="card dq-card mb-3">
        <div class="card-body">
          <div class="row g-3">
            <div :class="isEmployeeSelfService ? 'col-12' : 'col-12 col-lg-4'">
              <div class="dq-summary">
                <div class="dq-summary-title">
                  <i class="ti ti-cash-banknote me-1"></i> Overview
                </div>

                <div class="dq-metrics mt-2">
                  <div class="dq-metric">
                    <div class="dq-metric-label">Loans</div>
                    <div class="dq-metric-value">
                      {{ (store.total ?? dataSource.length) || 0 }}
                    </div>
                  </div>
                  <div class="dq-metric">
                    <div class="dq-metric-label">On Page</div>
                    <div class="dq-metric-value">{{ dataSource.length }}</div>
                  </div>
                </div>

                <div class="dq-note mt-2">
                  <div class="dq-note-title">
                    {{ isEmployeeSelfService ? "What you can do" : "Quick actions" }}
                  </div>
                  <div class="dq-note-sub">
                    {{
                      isEmployeeSelfService
                        ? "Review your balances and repayment history for each loan."
                        : "Create new loans if permitted, manage types, or review repayment history per loan."
                    }}
                  </div>
                </div>
              </div>
            </div>

            <div v-if="!isEmployeeSelfService" class="col-12 col-lg-8">
              <div class="dq-filters">
                <div class="dq-filters-head d-flex align-items-center gap-2">
                  <span class="dq-ic">
                    <i class="ti ti-filter"></i>
                  </span>
                  <div class="dq-filters-title">Filters</div>
                  <div class="ms-auto d-flex gap-2">
                    <button
                      class="btn dq-btn-ghost btn-sm"
                      type="button"
                      @click="clearFilters"
                      :disabled="store.loading"
                    >
                      <i class="ti ti-eraser me-1"></i> Clear
                    </button>
                    <button
                      class="btn dq-btn-outline btn-sm"
                      type="button"
                      @click="fetchLoans(1)"
                      :disabled="store.loading"
                    >
                      <i class="ti ti-search me-1"></i> Apply
                    </button>
                  </div>
                </div>

                <div class="row g-2 mt-2">
                  <div class="col-12 col-md-4">
                    <label class="form-label dq-label">Search</label>
                    <input
                      v-model="filters.search"
                      class="form-control dq-control"
                      placeholder="Employee name / ID, loan type, reference"
                      @keyup.enter="fetchLoans(1)"
                    />
                  </div>
                  <div class="col-12 col-md-4">
                    <label class="form-label dq-label">Status</label>
                    <select v-model="filters.status" class="form-select dq-select">
                      <option value="">All statuses</option>
                      <option value="active">Active</option>
                      <option value="paused">Paused</option>
                      <option value="closed">Closed</option>
                    </select>
                  </div>
                  <div class="col-12 col-md-4">
                    <label class="form-label dq-label">Deduction bucket</label>
                    <select v-model="filters.deduction_bucket" class="form-select dq-select">
                      <option value="">All buckets</option>
                      <option value="sacco_loan">SACCO loan</option>
                      <option value="helb">HELB</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>
                <div class="dq-hint mt-2 mb-0">
                  Tip: search by employee number, name, loan type, or reference text.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Table -->
      <div class="card dq-card">
        <div class="card-body">
          <div class="dq-table-head mb-2">
            <div class="dq-table-title">
              {{ pageTitle }}
              <span class="dq-table-sub"
                >{{ tableSubtext }}</span
              >
            </div>

            <div class="dq-table-actions d-flex align-items-center gap-2">
              <button
                class="btn dq-btn-ghost btn-sm"
                @click="fetchLoans(store.current)"
                :disabled="store.loading"
              >
                <i class="ti ti-refresh me-1"></i> Refresh
              </button>
            </div>
          </div>

          <div class="dq-table-wrap">
            <a-table
              class="dq-ant-table"
              :columns="columns"
              :data-source="dataSource"
              :loading="store.loading"
              :pagination="pagination"
              @change="onChange"
              row-key="key"
            >
              <template #bodyCell="{ column, record }">
                <template v-if="column.key === 'Employee'">
                  <div class="d-flex align-items-center gap-2">
                    <div class="dq-avatar">
                      <span>
                        {{
                          (record.employee_name || "E")
                            .slice(0, 1)
                            .toUpperCase()
                        }}
                      </span>
                    </div>
                    <div class="min-w-0">
                      <div class="fw-semibold text-dark text-truncate">
                        {{ record.employee_name || "—" }}
                      </div>
                      <div class="text-muted fs-12 text-truncate">
                        {{ record.employee_no || "—" }}
                      </div>
                    </div>
                  </div>
                </template>

                <template v-else-if="column.key === 'Type'">
                  <span class="badge dq-badge dq-neutral">
                    {{ record.loan_type_name || "—" }}
                  </span>
                </template>

                <template v-else-if="column.key === 'Balance'">
                  <span class="fw-semibold">
                    {{ currency }}
                    {{
                      Number(record.balance || 0).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                      })
                    }}
                  </span>
                </template>

                <template v-else-if="column.key === 'Status'">
                  <span
                    class="badge dq-badge"
                    :class="statusBadge(record.status)"
                  >
                    {{ record.status || "active" }}
                  </span>
                </template>

                <template v-else-if="column.key === 'Actions'">
                  <div class="d-flex justify-content-end gap-1">
                    <button
                      v-if="canEditLoan"
                      class="btn dq-icon-btn"
                      @click="openEdit(record.raw)"
                      title="Edit"
                    >
                      <i class="ti ti-edit"></i>
                    </button>
                    <button
                      v-if="canDeleteLoan"
                      class="btn dq-icon-btn dq-danger"
                      @click="removeLoan(record.id)"
                      title="Delete"
                    >
                      <i class="ti ti-trash"></i>
                    </button>
                    <button
                      v-if="canViewRepayments"
                      class="btn dq-icon-btn dq-primary"
                      @click="openRepayments(record.raw)"
                      title="Repayments"
                    >
                      <i class="ti ti-receipt"></i>
                    </button>
                  </div>
                </template>
              </template>
            </a-table>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- LOAN MODAL -->
  <div class="modal fade" id="loanModal" tabindex="-1">
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content dq-modal">
        <div class="modal-header dq-modal-header">
          <div class="d-flex align-items-center gap-2">
            <div class="dq-modal-ic"><i class="ti ti-cash-banknote"></i></div>
            <div>
              <div class="dq-modal-title">
                {{ form.id ? "Edit Loan" : "Create Loan" }}
              </div>
              <div class="dq-modal-sub">
                Configure principal, interest, method & repayment schedule.
              </div>
            </div>
          </div>
          <button
            type="button"
            class="btn-close btn-close-white"
            data-bs-dismiss="modal"
          />
        </div>

        <div class="modal-body">
          <div class="dq-callout mb-3">
            <div class="dq-callout-title">
              <i class="ti ti-bolt me-1"></i> Payroll integration
            </div>
            <div class="dq-callout-sub">
              Loans auto-apply into payroll. Repayments update balances.
            </div>
          </div>

          <div class="row g-3">
            <div class="col-md-7">
              <EmployeeSelectDropdown
                v-model="form.employee_id"
                :employees="employeeDropdownEmployees"
                mode="single"
                label="Employee"
                help="Select the employee taking the loan."
                :disabled="saving || isEmployeeSelfService"
              />
            </div>

            <div class="col-md-5">
              <label class="form-label dq-label">Loan Type</label>
              <select
                v-model="form.loan_type_id"
                class="form-select dq-select"
                :disabled="saving"
              >
                <option :value="null">— None —</option>
                <option v-for="t in loanTypes.rows" :key="t.id" :value="t.id">
                  {{ t.name }}
                </option>
              </select>
              <div class="dq-hint">
                Types can set defaults (method, bucket, interest).
              </div>
            </div>

            <div class="col-md-4">
              <label class="form-label dq-label">Loan Date</label>
              <input
                v-model="form.loan_date"
                class="form-control dq-control"
                type="date"
                :disabled="saving"
              />
            </div>

            <div class="col-md-4">
              <label class="form-label dq-label">Principal</label>
              <div class="dq-money-input">
                <span class="dq-money-prefix">{{ currency }}</span>
                <input
                  v-model.number="form.principal"
                  class="form-control dq-control"
                  type="number"
                  step="0.01"
                  min="0"
                  :disabled="saving"
                />
              </div>
            </div>

            <div class="col-md-4">
              <label class="form-label dq-label">Interest % p.a</label>
              <input
                v-model.number="form.annual_interest_rate"
                class="form-control dq-control"
                type="number"
                step="0.001"
                min="0"
                :disabled="saving"
              />
            </div>

            <div class="col-md-4">
              <label class="form-label dq-label">Months</label>
              <input
                v-model.number="form.repayment_months"
                class="form-control dq-control"
                type="number"
                min="1"
                :disabled="saving"
              />
            </div>

            <div class="col-md-4">
              <label class="form-label dq-label">Payment Method</label>
              <select
                v-model="form.payment_method"
                class="form-select dq-select"
                :disabled="saving"
              >
                <option value="straight_line">Straight Line</option>
                <option value="reducing_balance">Reducing Balance</option>
              </select>
            </div>

            <div class="col-md-4">
              <label class="form-label dq-label"
                >Payroll Deduction Category</label
              >
              <select
                v-model="form.deduction_bucket"
                class="form-select dq-select"
                :disabled="saving"
              >
                <option value="sacco_loan">SACCO Loan</option>
                <option value="helb">HELB</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div class="col-md-6">
              <label class="form-label dq-label">Installment</label>
              <div class="dq-money-input">
                <span class="dq-money-prefix">{{ currency }}</span>
                <input
                  v-model.number="form.installment_amount"
                  class="form-control dq-control"
                  type="number"
                  step="0.01"
                  min="0"
                  :disabled="saving"
                  @input="onInstallmentInput"
                />
              </div>
              <div class="dq-hint">
                {{
                  installmentManuallyEdited
                    ? "Custom amount — payroll will use this fixed deduction."
                    : "Auto-calculated from principal, rate, months, and method. Edit to override."
                }}
              </div>
              <button
                v-if="installmentManuallyEdited && suggestedInstallment > 0"
                type="button"
                class="btn btn-link btn-sm px-0 mt-1"
                @click="resetInstallmentToSuggested"
              >
                Use suggested {{ formatMoney(suggestedInstallment) }}
              </button>
              <button
                v-if="form.id && form.status === 'active' && canEditLoan"
                type="button"
                class="btn btn-link btn-sm px-0 mt-1 d-block"
                :disabled="payoffLoading"
                @click="applyFullPayoffInstallment"
              >
                Calculate full payoff for next payroll
              </button>
            </div>

            <div class="col-md-6">
              <label class="form-label dq-label">Start Deduct From</label>
              <input
                v-model="form.start_deduct_from"
                class="form-control dq-control"
                type="date"
                :disabled="saving"
              />
              <div class="dq-hint">
                Leave empty to start immediately from payroll logic.
              </div>
            </div>

            <div class="col-md-4">
              <label class="form-label dq-label">Status</label>
              <select
                v-model="form.status"
                class="form-select dq-select"
                :disabled="saving"
              >
                <option value="active">Active</option>
                <option value="paused">Paused</option>
                <option value="closed">Closed</option>
              </select>
            </div>

            <div class="col-md-8">
              <label class="form-label dq-label">Preview</label>
              <div class="dq-preview">
                <div class="dq-preview-top">
                  <span class="badge dq-badge dq-neutral">
                    {{
                      loanTypes.rows.find((t) => t.id === form.loan_type_id)
                        ?.name || "No Type"
                    }}
                  </span>
                  <span
                    class="badge dq-badge"
                    :class="statusBadge(form.status)"
                  >
                    {{ form.status }}
                  </span>
                </div>
                <div class="dq-preview-name">
                  {{ currency }}
                  {{
                    Number(form.principal || 0).toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                    })
                  }}
                </div>
                <div class="dq-preview-sub">
                  {{ form.payment_method.replaceAll("_", " ") }} •
                  {{ Number(form.repayment_months || 0) }} month(s) •
                  {{ Number(form.annual_interest_rate || 0).toFixed(3) }}% p.a
                </div>
                <div v-if="suggestedInstallment > 0" class="dq-preview-installment mt-2">
                  <span class="text-muted">Est. monthly deduction</span>
                  <strong>{{ formatMoney(displayInstallment) }}</strong>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer dq-modal-footer">
          <button
            class="btn dq-btn-ghost"
            data-bs-dismiss="modal"
            :disabled="saving"
          >
            Cancel
          </button>
          <a class="btn btn-primary" @click="save" :disabled="saving">
            <span
              v-if="saving"
              class="spinner-border spinner-border-sm me-2"
            ></span>
            <i v-else class="ti ti-device-floppy me-2"></i>
            {{ saving ? "Saving..." : "Save" }}
          </a>
        </div>
      </div>
    </div>
  </div>

  <!-- LOAN TYPES MODAL -->
  <div class="modal fade" id="loanTypesModal" tabindex="-1">
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content dq-modal">
        <div class="modal-header dq-modal-header">
          <div class="d-flex align-items-center gap-2">
            <div class="dq-modal-ic"><i class="ti ti-list"></i></div>
            <div>
              <div class="dq-modal-title">Loan Types</div>
              <div class="dq-modal-sub">
                Set defaults for faster and consistent loan creation.
              </div>
            </div>
          </div>
          <button
            type="button"
            class="btn-close btn-close-white"
            data-bs-dismiss="modal"
          />
        </div>

        <div class="modal-body">
          <div class="dq-section-title mb-2">
            <i class="ti ti-plus me-1"></i> Add Type
          </div>

          <div class="row g-2 align-items-end">
            <div class="col-md-4">
              <label class="form-label dq-label">Name</label>
              <input
                v-model="typeForm.name"
                class="form-control dq-control"
                placeholder="e.g SACCO Loan"
              />
            </div>
            <div class="col-md-2">
              <label class="form-label dq-label">Code</label>
              <input
                v-model="typeForm.code"
                class="form-control dq-control"
                placeholder="SACCO"
              />
            </div>
            <div class="col-md-3">
              <label class="form-label dq-label">Default Method</label>
              <select
                v-model="typeForm.default_method"
                class="form-select dq-select"
              >
                <option value="straight_line">Straight Line</option>
                <option value="reducing_balance">Reducing Balance</option>
              </select>
            </div>
            <div class="col-md-3">
              <label class="form-label dq-label">Default Bucket</label>
              <select
                v-model="typeForm.default_deduction_bucket"
                class="form-select dq-select"
              >
                <option value="sacco_loan">SACCO Loan</option>
                <option value="helb">HELB</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div class="col-md-3">
              <label class="form-label dq-label">Default % p.a</label>
              <input
                v-model.number="typeForm.default_annual_interest_rate"
                type="number"
                step="0.001"
                min="0"
                class="form-control dq-control"
              />
            </div>

            <div class="col-md-3">
              <label class="form-label dq-label">Active</label>
              <select
                v-model="typeForm.is_active"
                class="form-select dq-select"
              >
                <option :value="true">Yes</option>
                <option :value="false">No</option>
              </select>
            </div>

            <div class="col-md-6 text-end">
              <a
                v-if="canCreateLoanType"
                class="btn btn-primary"
                @click="saveType"
              >
                <i class="ti ti-plus me-1"></i> Add Type
              </a>
            </div>
          </div>

          <div class="dq-divider"></div>

          <div v-if="loanTypes.loading" class="text-muted">Loading...</div>

          <div v-else class="dq-table-wrap">
            <div class="table-responsive">
              <table class="table dq-table mb-0">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Method</th>
                    <th>Bucket</th>
                    <th class="text-end">% p.a</th>
                    <th>Active</th>
                    <th class="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="t in loanTypes.rows" :key="t.id" class="dq-row">
                    <td class="fw-semibold">{{ t.name }}</td>
                    <td class="text-capitalize">
                      {{ String(t.default_method || "").replaceAll("_", " ") }}
                    </td>
                    <td class="text-uppercase">
                      {{ t.default_deduction_bucket }}
                    </td>
                    <td class="text-end">
                      {{
                        Number(t.default_annual_interest_rate || 0).toFixed(3)
                      }}
                    </td>
                    <td>
                      <span
                        class="badge dq-badge"
                        :class="t.is_active ? 'dq-success' : 'dq-neutral'"
                      >
                        {{ t.is_active ? "Yes" : "No" }}
                      </span>
                    </td>
                    <td class="text-end">
                      <button
                        v-if="canDeleteLoanType"
                        class="btn dq-icon-btn dq-danger"
                        @click="removeType(t.id)"
                        title="Delete"
                      >
                        <i class="ti ti-trash"></i>
                      </button>
                    </td>
                  </tr>
                  <tr v-if="!loanTypes.rows.length">
                    <td colspan="6" class="text-center text-muted py-4">
                      No loan types yet.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div class="modal-footer dq-modal-footer">
          <button class="btn dq-btn-ghost" data-bs-dismiss="modal">
            Close
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- REPAYMENTS MODAL -->
  <div class="modal fade" id="loanRepaymentsModal" tabindex="-1">
    <div class="modal-dialog modal-xl modal-dialog-centered">
      <div class="modal-content dq-modal">
        <div class="modal-header dq-modal-header">
          <div class="d-flex align-items-center gap-2">
            <div class="dq-modal-ic"><i class="ti ti-receipt"></i></div>
            <div>
              <div class="dq-modal-title">Repayment History</div>
              <div class="dq-modal-sub">
                Track principal, interest, totals and balance after each
                deduction.
              </div>
            </div>
          </div>
          <button
            type="button"
            class="btn-close btn-close-white"
            data-bs-dismiss="modal"
          />
        </div>

        <div class="modal-body">
          <div
            class="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2 mb-2"
          >
            <div class="text-muted">
              Showing repayments for: <strong>{{ repaymentsTitle }}</strong>
              <span v-if="repaymentsActiveLoan?.balance != null" class="ms-2">
                · Balance <strong>{{ money(repaymentsActiveLoan.balance) }}</strong>
              </span>
            </div>
            <div class="d-flex flex-wrap align-items-center gap-2">
              <div v-if="canSettleActiveLoan" class="d-flex align-items-center gap-2">
                <label class="text-muted small mb-0">Payroll month</label>
                <input
                  v-model="repaymentsSettlePeriod"
                  class="form-control form-control-sm"
                  type="month"
                  style="max-width: 10rem"
                />
              </div>
              <button
                class="btn dq-btn-ghost btn-sm"
                @click="refreshRepayments"
                :disabled="repayments.loading"
              >
                <i class="ti ti-refresh me-1"></i> Refresh
              </button>
            </div>
          </div>

          <div v-if="repayments.loading" class="text-muted py-5 text-center">
            Loading...
          </div>

          <div v-else class="dq-table-wrap">
            <div class="table-responsive">
              <table class="table dq-table mb-0">
                <thead>
                  <tr>
                    <th>Period</th>
                    <th class="text-end">Opening</th>
                    <th class="text-end">Principal</th>
                    <th class="text-end">Interest</th>
                    <th class="text-end">Total</th>
                    <th class="text-end">Balance After</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="r in repayments.rows" :key="r.id" class="dq-row">
                    <td class="text-muted">{{ r.period || "—" }}</td>

                    <td class="text-end">{{ money(r.opening_balance) }}</td>

                    <td class="text-end">{{ money(r.principal_amount) }}</td>

                    <td class="text-end">{{ money(r.interest_amount) }}</td>

                    <td class="text-end fw-semibold">
                      {{ money(r.total_amount) }}
                    </td>

                    <td class="text-end">{{ money(r.closing_balance) }}</td>
                  </tr>

                  <tr v-if="!repayments.rows.length">
                    <td colspan="6" class="text-center text-muted py-4">
                      No repayments yet.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div class="modal-footer dq-modal-footer d-flex flex-wrap justify-content-between gap-2">
          <div class="text-muted small align-self-center">
            <span v-if="canSettleActiveLoan">
              Sets installment to full payoff; run payroll for that month to finish the loan.
            </span>
          </div>
          <div class="d-flex gap-2 ms-auto">
            <button
              v-if="canSettleActiveLoan"
              type="button"
              class="btn btn-primary btn-sm"
              :disabled="settlePreviewLoading"
              @click="beginSettleOnPayroll"
            >
              <span
                v-if="settlePreviewLoading"
                class="spinner-border spinner-border-sm me-1"
              ></span>
              <i v-else class="ti ti-cash me-1"></i>
              Settle on next payroll
            </button>
            <button class="btn dq-btn-ghost btn-sm" data-bs-dismiss="modal">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <LoanSettleConfirmModal ref="settleConfirmRef" @confirmed="onSettleConfirmed" />
</template>

<script setup>
import { computed, reactive, ref, onMounted, watch } from "vue";
import { Modal } from "bootstrap";
import { storeToRefs } from "pinia";
import { useLoansStore } from "@/stores/loansStore";
import { useLoanTypesStore } from "@/stores/loanTypesStore";
import { useLoanRepaymentsStore } from "@/stores/loanRepaymentsStore";
import EmployeeSelectDropdown from "@/components/common/EmployeeSelectDropdown.vue";
import LoanSettleConfirmModal from "@/components/modal/loan-settle-confirm-modal.vue";
import { useEmployeeStore } from "@/stores/employeeStore";
import { useCompanyStore } from "@/stores/companyStore";
import { useAuthStore } from "@/stores/authStore";
import {
  calculateMonthlyInstallment,
  roundMoney,
} from "@/utils/loanCalculations";
import { toast } from "@/utils/toast";

const store = useLoansStore();
const loanTypes = useLoanTypesStore();
const employeeStore = useEmployeeStore();
const companyStore = useCompanyStore();
const repayments = useLoanRepaymentsStore();
const authStore = useAuthStore();

const { companies, activeCompanyId } = storeToRefs(companyStore);
const { user } = storeToRefs(authStore);

const activeCompany = computed(() =>
  (companies.value || []).find((c) => c.id === activeCompanyId.value),
);

const currency = computed(() => activeCompany.value?.currency_code || "KES");
const currentEmployeeId = computed(() => Number(user.value?.employee?.id || 0) || null);
const isEmployeeSelfService = computed(() => {
  const roles = (user.value?.roles || []).map((r) =>
    typeof r === "string" ? r : r?.name,
  );
  return roles.includes("Employee") && !!currentEmployeeId.value;
});
const canCreateLoan = computed(() => authStore.can("Loan.Create"));
const canEditLoan = computed(() => authStore.can("Loan.Edit"));
const canDeleteLoan = computed(() => authStore.can("Loan.Delete"));
const canReadLoan = computed(
  () => authStore.can("Loan.Read") || isEmployeeSelfService.value,
);
const canReadLoanTypes = computed(() => authStore.can("LoanType.Read"));
const canCreateLoanType = computed(() => authStore.can("LoanType.Create"));
const canDeleteLoanType = computed(() => authStore.can("LoanType.Delete"));
const canManageLoanTypes = computed(
  () => canReadLoanTypes.value || canCreateLoanType.value || canDeleteLoanType.value,
);
const canViewRepayments = computed(() => canReadLoan.value);
const canSettleActiveLoan = computed(() => {
  if (!canEditLoan.value || isEmployeeSelfService.value) return false;
  const loan = repaymentsActiveLoan.value;
  if (!loan) return false;
  return String(loan.status || "").toLowerCase() === "active"
    && Number(loan.balance || 0) > 0;
});
const showActionsColumn = computed(
  () => canEditLoan.value || canDeleteLoan.value || canViewRepayments.value,
);
const pageTitle = computed(() =>
  isEmployeeSelfService.value ? "My Loans" : "Loans",
);
const pageSection = computed(() =>
  isEmployeeSelfService.value ? "Employee" : "HR",
);
const pageCrumb = computed(() =>
  isEmployeeSelfService.value ? "My Loans" : "Loans",
);
const pageSubtext = computed(() =>
  isEmployeeSelfService.value
    ? "Your payroll-linked loans and repayments are shown here."
    : "Loans auto-apply into payroll. Repayments update balances automatically.",
);
const tableSubtext = computed(() =>
  isEmployeeSelfService.value
    ? "View your loan balances and repayment history"
    : "Manage employee loans & repayments",
);

let repaymentsModal;
const repaymentsTitle = ref("");
const repaymentsQuery = reactive({ loan_id: null, employee_id: null });
const repaymentsActiveLoan = ref(null);
const repaymentsSettlePeriod = ref(new Date().toISOString().slice(0, 7));
const settleConfirmRef = ref(null);
const settlePreviewLoading = ref(false);
const payoffLoading = ref(false);

const saving = ref(false);
const installmentManuallyEdited = ref(false);

const filters = reactive({
  search: "",
  status: "",
  deduction_bucket: "",
});
let modal;
let typesModal;


function toDateInput(value) {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d)) return "";
  return d.toISOString().slice(0, 10);
}


const form = reactive({
  id: null,
  employee_id: "",
  loan_type_id: null,
  loan_date: "",
  principal: 0,
  annual_interest_rate: 0,
  repayment_months: 12,
  payment_method: "straight_line",
  installment_amount: null,
  start_deduct_from: "",
  deduction_bucket: "sacco_loan",
  status: "active",
});

const employeeDropdownEmployees = computed(() =>
  (employeeStore.employees || []).map((e) => ({
    id: e.id,
    name: `${e.first_name || ""} ${e.last_name || ""}`.trim() || e.name || "—",
    employee_id: e.employee_id || e.employee_no || e.staff_no || "",
    avatar_url: e.avatar_url || e.photo_url || null,
    department: e.department?.name || e.department_name || "",
    designation: e.designation?.name || e.designation_name || "",
  })),
);

const typeForm = reactive({
  name: "",
  code: "",
  default_method: "straight_line",
  default_annual_interest_rate: 0,
  default_deduction_bucket: "sacco_loan",
  is_active: true,
});

const columns = computed(() => {
  const base = [
    { title: "Type", key: "Type" },
    { title: "Method", dataIndex: "payment_method", key: "Method" },
    { title: "Months", dataIndex: "repayment_months", key: "Months" },
    { title: "Balance", key: "Balance" },
    { title: "Status", dataIndex: "status", key: "Status" },
  ];

  if (!isEmployeeSelfService.value) {
    base.unshift({ title: "Employee", key: "Employee" });
  }

  if (showActionsColumn.value) {
    base.push({ title: "Actions", key: "Actions" });
  }

  return base;
});

const dataSource = computed(() =>
  (store.rows || []).map((r, idx) => ({
    key: r.id || idx,
    id: r.id,
    employee_no: r.employee?.employee_id,
    employee_name: `${r.employee?.first_name || ""} ${
      r.employee?.last_name || ""
    }`.trim(),
    loan_type_name: r.type?.name,
    payment_method: r.payment_method,
    repayment_months: r.repayment_months,
    balance: r.balance,
    status: r.status,
    raw: r,
  })),
);

const pagination = computed(() => ({
  total: store.total,
  pageSize: store.pageSize,
  current: store.current,
  showTotal: (t) => `Total ${t} items`,
}));

function loanListParams(page = 1, perPage = store.pageSize) {
  return {
    page,
    per_page: perPage,
    search: filters.search?.trim() || undefined,
    status: filters.status || undefined,
    deduction_bucket: filters.deduction_bucket || undefined,
    ...(isEmployeeSelfService.value && currentEmployeeId.value
      ? { employee_id: currentEmployeeId.value }
      : {}),
  };
}

function fetchLoans(page = 1, perPage = store.pageSize) {
  return store.fetch(loanListParams(page, perPage));
}

function clearFilters() {
  filters.search = "";
  filters.status = "";
  filters.deduction_bucket = "";
  fetchLoans(1);
}

function onChange(p) {
  fetchLoans(p.current, p.pageSize);
}

function resetForm() {
  form.id = null;
  form.employee_id = "";
  form.loan_type_id = null;
  form.loan_date = "";
  form.principal = 0;
  form.annual_interest_rate = 0;
  form.repayment_months = 12;
  form.payment_method = "straight_line";
  form.installment_amount = null;
  form.start_deduct_from = "";
  form.deduction_bucket = "sacco_loan";
  form.status = "active";
  installmentManuallyEdited.value = false;
}

function openCreate() {
  if (!canCreateLoan.value) return;
  resetForm();
  if (isEmployeeSelfService.value && currentEmployeeId.value) {
    form.employee_id = currentEmployeeId.value;
  }
  syncSuggestedInstallment();
  modal?.show();
}

function openEdit(row) {
  if (!canEditLoan.value) return;
  form.id = row.id;
  form.employee_id = row.employee_id;
  form.loan_type_id = row.loan_type_id || null;
  form.loan_date = toDateInput(row.loan_date);
  form.principal = Number(row.principal || 0);
  form.annual_interest_rate = Number(row.annual_interest_rate || 0);
  form.repayment_months = Number(row.repayment_months || 12);
  form.payment_method = row.payment_method || "straight_line";
  form.installment_amount = row.installment_amount
    ? Number(row.installment_amount)
    : null;
  form.start_deduct_from = toDateInput(row.start_deduct_from) || "";
  form.deduction_bucket = row.deduction_bucket || "sacco_loan";
  form.status = row.status || "active";
  installmentManuallyEdited.value = !!row.installment_amount;
  modal?.show();
}

async function save() {
  if (!canCreateLoan.value && !(form.id && canEditLoan.value)) return;
  saving.value = true;
  try {
    const payload = {
      employee_id: Number(form.employee_id),
      loan_type_id: form.loan_type_id ? Number(form.loan_type_id) : null,
      loan_date: form.loan_date,
      principal: Number(form.principal),
      annual_interest_rate: Number(form.annual_interest_rate || 0),
      repayment_months: Number(form.repayment_months),
      payment_method: form.payment_method,
      installment_amount: form.installment_amount
        ? Number(form.installment_amount)
        : null,
      start_deduct_from: form.start_deduct_from || null,
      deduction_bucket: form.deduction_bucket,
      status: form.status,
    };

    if (!payload.employee_id || !payload.loan_date || !payload.principal)
      return;

    if (form.id) await store.update(form.id, payload);
    else await store.create(payload);

    modal?.hide();
    await fetchLoans(1);
  } finally {
    saving.value = false;
  }
}

async function removeLoan(id) {
  if (!canDeleteLoan.value) return;
  if (!confirm("Delete this loan?")) return;
  await store.destroy(id);
  await fetchLoans(store.current);
}

function openTypes() {
  if (!canManageLoanTypes.value) return;
  typesModal?.show();
}

async function saveType() {
  if (!canCreateLoanType.value) return;
  if (!typeForm.name) return;

  await loanTypes.create({
    name: typeForm.name,
    code: typeForm.code || null,
    default_method: typeForm.default_method,
    default_annual_interest_rate: Number(
      typeForm.default_annual_interest_rate || 0,
    ),
    default_deduction_bucket: typeForm.default_deduction_bucket,
    is_active: !!typeForm.is_active,
  });

  typeForm.name = "";
  typeForm.code = "";
  typeForm.default_method = "straight_line";
  typeForm.default_annual_interest_rate = 0;
  typeForm.default_deduction_bucket = "sacco_loan";
  typeForm.is_active = true;

  await loanTypes.fetch();
}

async function removeType(id) {
  if (!canDeleteLoanType.value) return;
  if (!confirm("Delete this loan type?")) return;
  await loanTypes.destroy(id);
  await loanTypes.fetch();
}

const suggestedInstallment = computed(() =>
  calculateMonthlyInstallment({
    principal: form.principal,
    annualInterestRatePct: form.annual_interest_rate,
    repaymentMonths: form.repayment_months,
    paymentMethod: form.payment_method,
  }),
);

const displayInstallment = computed(() => {
  const manual = Number(form.installment_amount);
  if (installmentManuallyEdited.value && manual > 0) {
    return roundMoney(manual);
  }
  return suggestedInstallment.value;
});

function syncSuggestedInstallment() {
  if (installmentManuallyEdited.value) return;
  const next = suggestedInstallment.value;
  form.installment_amount = next > 0 ? next : null;
}

function onInstallmentInput() {
  installmentManuallyEdited.value = true;
}

function resetInstallmentToSuggested() {
  installmentManuallyEdited.value = false;
  syncSuggestedInstallment();
}

function formatMoney(amount) {
  return (
    currency.value +
    " " +
    Number(amount ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 })
  );
}

function money(v) {
  return formatMoney(v);
}

function statusBadge(status) {
  const s = String(status || "active").toLowerCase();
  if (s === "closed") return "dq-danger";
  if (s === "paused") return "dq-warning";
  return "dq-success";
}

function openRepayments(loanRow) {
  if (!canViewRepayments.value) return;
  repaymentsQuery.loan_id = loanRow.id;
  repaymentsQuery.employee_id = loanRow.employee_id;
  repaymentsActiveLoan.value = loanRow;
  repaymentsSettlePeriod.value = new Date().toISOString().slice(0, 7);
  repaymentsTitle.value = `Loan #${loanRow.id}`;
  repaymentsModal?.show();
  refreshRepayments();
}

async function fetchPayoffPreview(loanId, period) {
  return store.payoffPreview(loanId, period ? { period } : {});
}

async function applyFullPayoffInstallment() {
  if (!form.id || payoffLoading.value) return;
  payoffLoading.value = true;
  try {
    const data = await fetchPayoffPreview(form.id);
    const total = Number(data?.breakdown?.total_installment || 0);
    if (total <= 0) return;
    form.installment_amount = total;
    installmentManuallyEdited.value = true;
    toast(`Installment set to full payoff (${formatMoney(total)}). Save the loan to keep this.`, "success");
  } catch (e) {
    toast(e?.response?.data?.message || e?.message || "Could not calculate payoff.", "error");
  } finally {
    payoffLoading.value = false;
  }
}

async function beginSettleOnPayroll() {
  const loan = repaymentsActiveLoan.value;
  if (!loan?.id || settlePreviewLoading.value) return;
  settlePreviewLoading.value = true;
  try {
    const data = await fetchPayoffPreview(loan.id, repaymentsSettlePeriod.value);
    settleConfirmRef.value?.open({
      id: loan.id,
      preview: data,
      currencyCode: currency.value,
    });
  } catch (e) {
    toast(e?.response?.data?.message || e?.message || "Could not load payoff preview.", "error");
  } finally {
    settlePreviewLoading.value = false;
  }
}

async function onSettleConfirmed({ loanId, period }) {
  const modal = settleConfirmRef.value;
  modal?.setSubmitting(true);
  try {
    const data = await store.settleOnNextPayroll(loanId, { period });
    if (data?.loan) {
      repaymentsActiveLoan.value = { ...repaymentsActiveLoan.value, ...data.loan };
    }
    modal?.hide();
    await refreshRepayments();
  } catch (e) {
    // toast handled in store
  } finally {
    modal?.setSubmitting(false);
  }
}

async function refreshRepayments() {
  await repayments.fetch({
    employee_id: repaymentsQuery.employee_id,
    loan_id: repaymentsQuery.loan_id,
    per_page: 200,
  });
}

onMounted(async () => {
  modal = new Modal(document.getElementById("loanModal"));
  typesModal = new Modal(document.getElementById("loanTypesModal"));
  repaymentsModal = new Modal(document.getElementById("loanRepaymentsModal"));

  const tasks = [fetchLoans(1, 25)];

  if (canManageLoanTypes.value) {
    tasks.push(loanTypes.fetch({}));
  }

  if (canCreateLoan.value || canEditLoan.value) {
    tasks.push(
      employeeStore.fetchEmployees(
        isEmployeeSelfService.value && currentEmployeeId.value
          ? { employee_id: currentEmployeeId.value, per_page: 5000 }
          : { per_page: 5000 },
      ),
    );
  }

  await Promise.all(tasks);
});

watch(
  () => form.loan_type_id,
  (id) => {
    if (!id) return;
    const t = loanTypes.rows.find((x) => x.id === id);
    if (!t) return;

    // Only set defaults when creating (or when fields empty)
    if (!form.id) {
      form.payment_method = t.default_method || form.payment_method;
      form.annual_interest_rate = Number(t.default_annual_interest_rate || 0);
      form.deduction_bucket =
        t.default_deduction_bucket || form.deduction_bucket;
      installmentManuallyEdited.value = false;
      syncSuggestedInstallment();
    }
  },
);

watch(
  () => [
    form.principal,
    form.annual_interest_rate,
    form.repayment_months,
    form.payment_method,
  ],
  () => {
    syncSuggestedInstallment();
  },
);
</script>

<style scoped>
/* ================= Premium Theme (Loans) ================= */
.dq-loans {
  --dq-border: rgba(15, 23, 42, 0.1);
  --dq-soft: rgba(15, 23, 42, 0.06);
  --dq-text: #0f172a;
  --dq-muted: rgba(15, 23, 42, 0.65);
  --dq-muted2: rgba(15, 23, 42, 0.45);
  --dq-shadow: 0 14px 35px rgba(2, 6, 23, 0.08);
  --dq-shadow2: 0 10px 24px rgba(2, 6, 23, 0.08);
  --dq-radius: 18px;
}

.dq-topbar {
  padding: 14px 14px 0 14px;
}
.dq-subtext {
  font-size: 12px;
  color: var(--dq-muted);
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  border: 1px solid var(--dq-soft);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(8px);
}

.dq-card {
  border: 1px solid var(--dq-border) !important;
  border-radius: var(--dq-radius) !important;
  box-shadow: var(--dq-shadow2);
  overflow: hidden;
}

/* Buttons */
.dq-btn-primary {
  background: #0f172a !important;
  border-color: #0f172a !important;
  color: #fff !important;
  box-shadow: 0 10px 20px rgba(15, 23, 42, 0.18);
}
.dq-btn-primary:hover {
  transform: translateY(-1px);
}

.dq-btn-ghost {
  background: rgba(15, 23, 42, 0.06) !important;
  border: 1px solid rgba(15, 23, 42, 0.1) !important;
  color: #0f172a !important;
}
.dq-btn-ghost:hover {
  background: rgba(15, 23, 42, 0.08) !important;
}

/* Summary */
.dq-summary {
  border: 1px solid var(--dq-soft);
  border-radius: 16px;
  padding: 14px;
  background: radial-gradient(
      900px 220px at 10% 0%,
      rgba(15, 23, 42, 0.06),
      transparent 55%
    ),
    linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.92),
      rgba(255, 255, 255, 0.85)
    );
}
.dq-summary-title {
  font-weight: 900;
  color: var(--dq-text);
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.dq-metrics {
  display: flex;
  gap: 10px;
}
.dq-metric {
  flex: 1;
  border: 1px solid var(--dq-soft);
  background: rgba(255, 255, 255, 0.9);
  border-radius: 14px;
  padding: 10px;
}
.dq-metric-label {
  font-size: 12px;
  color: var(--dq-muted2);
}
.dq-metric-value {
  font-size: 16px;
  font-weight: 900;
  color: var(--dq-text);
  margin-top: 2px;
}

.dq-note {
  border: 1px solid rgba(15, 23, 42, 0.1);
  background: rgba(15, 23, 42, 0.03);
  border-radius: 14px;
  padding: 10px;
}
.dq-note-title {
  font-weight: 900;
  font-size: 12px;
  color: var(--dq-text);
}
.dq-note-sub {
  font-size: 12px;
  color: var(--dq-muted);
  margin-top: 2px;
}

.dq-filters {
  border: 1px solid var(--dq-soft);
  border-radius: 16px;
  padding: 14px;
  background: rgba(255, 255, 255, 0.92);
}
.dq-filters-head {
  padding-bottom: 10px;
  border-bottom: 1px dashed rgba(15, 23, 42, 0.14);
}
.dq-ic {
  width: 34px;
  height: 34px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(15, 23, 42, 0.06);
  border: 1px solid rgba(15, 23, 42, 0.1);
  color: #0f172a;
}
.dq-filters-title {
  font-weight: 800;
  color: var(--dq-text);
}
.dq-label {
  font-size: 12px;
  font-weight: 700;
  color: var(--dq-muted);
}
.dq-hint {
  font-size: 11px;
  color: var(--dq-muted2);
  margin-top: 6px;
}
.dq-btn-outline {
  background: #fff !important;
  border: 1px solid rgba(15, 23, 42, 0.14) !important;
  color: #0f172a !important;
}

/* Help */
.dq-help {
  border: 1px solid var(--dq-soft);
  border-radius: 16px;
  padding: 14px;
  background: rgba(255, 255, 255, 0.92);
}
.dq-help-title {
  font-weight: 900;
  color: var(--dq-text);
}
.dq-help-sub {
  font-size: 12px;
  color: var(--dq-muted);
  margin-top: 4px;
}
.dq-help-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.dq-pill {
  border: 1px solid rgba(15, 23, 42, 0.12);
  background: rgba(15, 23, 42, 0.05);
  color: var(--dq-text);
  border-radius: 999px;
  padding: 7px 10px;
  font-weight: 700;
}

/* Table head + wrap */
.dq-table-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}
.dq-table-title {
  font-weight: 900;
  color: var(--dq-text);
}
.dq-table-sub {
  font-weight: 700;
  color: var(--dq-muted2);
  font-size: 12px;
  margin-left: 8px;
}

.dq-table-wrap {
  border: 1px solid var(--dq-soft);
  border-radius: 16px;
  overflow: hidden;
  background: #fff;
}

/* Ant table polish */
:deep(.dq-ant-table) {
  background: transparent;
}
:deep(.ant-table) {
  background: transparent;
}
:deep(.ant-table-container) {
  border-radius: 16px;
}
:deep(.ant-table-thead > tr > th) {
  background: linear-gradient(180deg, #ffffff, #fbfbfc) !important;
  border-bottom: 1px solid rgba(15, 23, 42, 0.12) !important;
  color: rgba(15, 23, 42, 0.75) !important;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
:deep(.ant-table-tbody > tr > td) {
  border-bottom: 1px solid rgba(15, 23, 42, 0.06) !important;
}
:deep(.ant-table-tbody > tr:hover > td) {
  background: rgba(15, 23, 42, 0.03) !important;
}

/* Badges + avatar */
.dq-avatar {
  width: 36px;
  height: 36px;
  border-radius: 14px;
  border: 1px solid rgba(15, 23, 42, 0.1);
  background: rgba(15, 23, 42, 0.06);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 900;
  color: #0f172a;
  flex: 0 0 auto;
}
.dq-badge {
  border-radius: 999px !important;
  padding: 7px 10px !important;
  font-weight: 800 !important;
  border: 1px solid rgba(15, 23, 42, 0.12) !important;
}
.dq-neutral {
  background: rgba(15, 23, 42, 0.05) !important;
  color: #0f172a !important;
}
.dq-success {
  background: rgba(16, 185, 129, 0.12) !important;
  color: #047857 !important;
}
.dq-warning {
  background: rgba(245, 158, 11, 0.14) !important;
  color: #92400e !important;
}
.dq-danger {
  background: rgba(239, 68, 68, 0.12) !important;
  color: #b91c1c !important;
}
.dq-primary {
  background: rgba(59, 130, 246, 0.1) !important;
  color: #1d4ed8 !important;
}

/* Icon buttons */
.dq-icon-btn {
  width: 36px;
  height: 36px;
  border-radius: 12px;
  border: 1px solid rgba(15, 23, 42, 0.12);
  background: rgba(15, 23, 42, 0.05);
  color: #0f172a;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.dq-icon-btn:hover {
  background: rgba(15, 23, 42, 0.08);
}
.dq-icon-btn.dq-danger {
  border-color: rgba(239, 68, 68, 0.22);
  background: rgba(239, 68, 68, 0.08);
  color: #b91c1c;
}
.dq-icon-btn.dq-primary {
  border-color: rgba(59, 130, 246, 0.2);
  background: rgba(59, 130, 246, 0.1);
  color: #1d4ed8;
}

/* Inputs */
.dq-label {
  font-size: 12px;
  font-weight: 800;
  color: var(--dq-muted);
}
.dq-hint {
  font-size: 11px;
  color: var(--dq-muted2);
  margin-top: 6px;
}
.dq-select,
.dq-control {
  border-radius: 12px !important;
  border: 1px solid rgba(15, 23, 42, 0.14) !important;
  box-shadow: none !important;
}

/* Modal */
.dq-modal {
  border-radius: 18px;
  overflow: hidden;
  border: 1px solid rgba(15, 23, 42, 0.14);
  box-shadow: var(--dq-shadow);
}
.dq-modal-header {
  border: 0;
  color: #fff;
  padding: 16px 18px;
  background: radial-gradient(
      900px 260px at 10% 0%,
      rgba(255, 255, 255, 0.2),
      transparent 55%
    ),
    linear-gradient(135deg, #0f172a, #111827);
}
.dq-modal-ic {
  width: 44px;
  height: 44px;
  border-radius: 16px;
  display: grid;
  place-items: center;
  background: rgba(255, 255, 255, 0.16);
  border: 1px solid rgba(255, 255, 255, 0.2);
}
.dq-modal-title {
  font-weight: 900;
  line-height: 1.1;
}
.dq-modal-sub {
  font-size: 12px;
  opacity: 0.85;
}

.dq-callout {
  border-radius: 16px;
  border: 1px solid rgba(59, 130, 246, 0.18);
  background: rgba(59, 130, 246, 0.08);
  padding: 12px 14px;
}
.dq-callout-title {
  font-weight: 900;
  color: #0f172a;
}
.dq-callout-sub {
  margin-top: 4px;
  font-size: 12px;
  color: rgba(15, 23, 42, 0.75);
}

.dq-divider {
  height: 1px;
  background: rgba(15, 23, 42, 0.1);
  margin: 16px 0;
}

.dq-preview {
  border: 1px solid rgba(15, 23, 42, 0.12);
  background: rgba(15, 23, 42, 0.03);
  border-radius: 16px;
  padding: 12px;
}
.dq-preview-top {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}
.dq-preview-name {
  font-weight: 900;
  margin-top: 8px;
  color: var(--dq-text);
}
.dq-preview-sub {
  font-size: 12px;
  color: var(--dq-muted);
  margin-top: 4px;
}

.dq-preview-installment {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding-top: 10px;
  border-top: 1px dashed rgba(15, 23, 42, 0.12);
  font-size: 13px;
}

.dq-preview-installment strong {
  color: var(--dq-text);
}

.dq-money-input {
  position: relative;
}
.dq-money-prefix {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 12px;
  font-weight: 900;
  color: rgba(15, 23, 42, 0.7);
  padding: 4px 8px;
  border-radius: 10px;
  background: rgba(15, 23, 42, 0.06);
  border: 1px solid rgba(15, 23, 42, 0.1);
}
.dq-money-input input {
  padding-left: 78px !important;
}

.dq-modal-footer {
  border-top: 1px solid rgba(15, 23, 42, 0.1);
  background: rgba(255, 255, 255, 0.9);
  gap: 10px;
}
</style>
